import * as React from 'react'
import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { LeaveInfoEmployeeNamesDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { dateUIToBackend } from 'modules/core/lib/dates'

import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import leaves from 'modules/timeoff/store/collections/leaves'

import LeaveOverlaps, { MAX_EMPLOYEE_USER_NAMES, OverlapsInfo } from './LeaveOverlaps'

interface Props {
  form: LeaveForm
  employeeId: number
}

const LeaveInfo = ({ form, employeeId }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [fetchEmployeeNames] = useLazyQuery(LeaveInfoEmployeeNamesDocument)

  const [overlapsEmployees, setOverlapsEmployees] = useState<{
    inTeam: OverlapsInfo
    company: OverlapsInfo
  }>({
    inTeam: {
      userNames: [],
      total: 0,
    },
    company: {
      userNames: [],
      total: 0,
    },
  })

  const startOn = form.get('start_on').value
  const finishOn = form.get('finish_on').value

  const canSeeCalendar = !currentEmployee || hasRegularAccess(currentEmployee.hasRegularAccess)

  React.useEffect(() => {
    if (!startOn || !finishOn || !canSeeCalendar) return

    const formattedStartOn = dateUIToBackend(startOn)
    const formattedFinishOn = dateUIToBackend(finishOn)

    const fetchData = async () => {
      const data = await leaves.rpc('overlaps', {
        employee_id: employeeId,
        from: formattedStartOn,
        to: formattedFinishOn,
      })

      const { data: overlapsEmployeeNames } = await fetchEmployeeNames({
        variables: {
          employeeIds: data.overlaps.sort(),
          maxEmployeeNames: MAX_EMPLOYEE_USER_NAMES,
        },
        fetchPolicy: 'cache-first',
      })

      const { data: inTeamEmployeeNames } = await fetchEmployeeNames({
        variables: {
          employeeIds: data.in_team.sort(),
          maxEmployeeNames: MAX_EMPLOYEE_USER_NAMES,
        },
        fetchPolicy: 'cache-first',
      })

      setOverlapsEmployees({
        company: {
          userNames:
            overlapsEmployeeNames?.employees.employeeNamesConnection.edges.map(
              (edge) => edge.node.fullName
            ) ?? [],
          total: data.overlaps.length,
        },
        inTeam: {
          userNames:
            inTeamEmployeeNames?.employees.employeeNamesConnection.edges.map(
              (edge) => edge.node.fullName
            ) ?? [],
          total: data.in_team.length,
        },
      })
    }

    fetchData()
  }, [employeeId, finishOn, startOn])

  if (!overlapsEmployees.company.total) return null

  return (
    <Box
      paddingX="s24"
      paddingY="s16"
      background="grey400"
      borderRadius={{
        all: 'abs008',
      }}
    >
      <LeaveOverlaps
        employeeId={employeeId}
        company={overlapsEmployees.company}
        inTeam={overlapsEmployees.inTeam}
      />
    </Box>
  )
}

export default observer(LeaveInfo)
